import { loginResponse, sessionTokens } from '@occ/contracts';
import type { AuthProvider } from '@refinedev/core';
import { HttpError, post, request } from '../api/http';
import { ROUTES } from '../api/routes';
import { closeLiveClient } from '../live/socket';
import { clearCurrentUser, getCurrentUser } from './identity';
import {
  clearSession,
  getPendingMfaToken,
  loadSession,
  saveSession,
  setPendingMfaToken,
} from './session';

/**
 * `authProvider` de Refine contra el Módulo 9.
 *
 * El login puede terminar en dos sitios —sesión emitida o desafío de MFA— y el
 * contrato de `@occ/contracts` lo modela como unión discriminada justo para que
 * el panel no pueda olvidarse del segundo caso. Aquí se traduce a lo que Refine
 * entiende: `success: false` con el aviso de que falta el segundo factor.
 */

export type PasswordLogin = { kind: 'password'; email: string; password: string; totp?: string };
export type MfaLogin = { kind: 'mfa'; totp: string };
export type LoginParams = PasswordLogin | MfaLogin;

/** Campo extra que la pantalla de login lee del resultado de `useLogin`. */
export type LoginResult = { mfaRequired?: boolean };

const asLoginParams = (params: unknown): LoginParams | null => {
  if (typeof params !== 'object' || params === null) return null;
  const candidate = params as Partial<PasswordLogin> & Partial<MfaLogin>;
  if (candidate.kind === 'password' && candidate.email && candidate.password) {
    return {
      kind: 'password',
      email: candidate.email,
      password: candidate.password,
      totp: candidate.totp,
    };
  }
  if (candidate.kind === 'mfa' && candidate.totp) return { kind: 'mfa', totp: candidate.totp };
  return null;
};

const failure = (message: string) => ({
  success: false as const,
  error: { name: 'Error de autenticación', message },
});

export const authProvider: AuthProvider = {
  async login(params: unknown) {
    const parsed = asLoginParams(params);
    if (!parsed) return failure('Faltan credenciales');

    try {
      // Las dos rutas no devuelven lo mismo: `login` puede acabar en desafío y
      // por eso responde la unión del contrato; `mfa/verify` solo puede acabar
      // en sesión y responde los tokens pelados.
      let tokens;
      if (parsed.kind === 'password') {
        const response = await post(
          ROUTES.login,
          { email: parsed.email, password: parsed.password, totp: parsed.totp },
          loginResponse,
        );

        if (response.status === 'mfa_required') {
          setPendingMfaToken(response.mfaToken);
          return {
            success: false,
            mfaRequired: true,
            error: {
              name: 'MFA',
              message: 'Introduce el código de tu aplicación de autenticación',
            },
          };
        }
        tokens = response.tokens;
      } else {
        tokens = await post(
          ROUTES.mfaVerify,
          { mfaToken: getPendingMfaToken(), totp: parsed.totp },
          sessionTokens,
        );
      }

      setPendingMfaToken(null);
      saveSession(tokens);
      clearCurrentUser();
      await getCurrentUser();
      return { success: true, redirectTo: '/' };
    } catch (error) {
      if (error instanceof HttpError && error.status === 0) {
        return failure('No se puede contactar con el backend');
      }
      return failure(error instanceof Error ? error.message : 'Error desconocido');
    }
  },

  async logout() {
    if (loadSession()) {
      // Hoy el backend solo lo audita: no hay lista de revocación y el token de
      // acceso vive hasta que vence. Si no contesta, la sesión local se borra igual.
      await request({ method: 'POST', path: ROUTES.logout }).catch(() => undefined);
    }
    clearSession();
    clearCurrentUser();
    setPendingMfaToken(null);
    closeLiveClient();
    return { success: true, redirectTo: '/login' };
  },

  async check() {
    if (!loadSession()) {
      return { authenticated: false, redirectTo: '/login', logout: false };
    }
    const user = await getCurrentUser();
    if (!user) {
      clearSession();
      return { authenticated: false, redirectTo: '/login', logout: true };
    }
    return { authenticated: true };
  },

  async onError(error: unknown) {
    if (error instanceof HttpError && error.status === 401) {
      clearSession();
      clearCurrentUser();
      return { logout: true, redirectTo: '/login', error };
    }
    // Un 403 es una decisión legítima de la puerta, no una sesión rota.
    return {};
  },

  async getPermissions() {
    return (await getCurrentUser())?.permissions ?? [];
  },

  async getIdentity() {
    return await getCurrentUser();
  },
};
